import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useOutletContext, useParams, useSearchParams } from '@remix-run/react';
import type { Tier as DreamTier } from '@shared/dream-components/src';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast from '~/hooks/useToast';
import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import type { OutletContext } from '~/interfaces/outlet_context';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME } from '~/utils/nectarTrackingFields';

type Props = {
  showSubmitButton?: boolean;
  onSuccess?: () => void;
  successToastMessage?: string;
  children?: ({ isLoading, submitForm }: { isLoading: boolean; submitForm: () => void }) => ReactNode;
  tier?: DreamTier;
};

const DowngradeAction = ({ children, onSuccess, tier, successToastMessage, showSubmitButton = true }: Props) => {
  const fetcher = useFetcher<{
    toast?: any;
  }>();
  const params = useParams();
  const { subscriberId } = params;
  const fetcherData = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();
  const { removeCookie, cookies } = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();
  const nectarPageBlock = searchParams.get(NECTAR_PAGE_BLOCK_GET_PARAM_NAME) || undefined;
  const status = fetcherData?.toast?.status;

  useToast(fetcherData?.toast, onSuccess);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want this to run on every render
  useEffect(() => {
    if (status === 'success') {
      removeCookie(ACTIVE_UPGRADE_COOKIE_NAME);
    }
  }, [status]);

  const submitForm = () => {
    if (formRef.current) {
      fetcher.submit(formRef.current);
    }
  };

  return (
    <fetcher.Form ref={formRef} method="post" action="/downgrade">
      <input hidden name="subscriber_id" defaultValue={subscriberId} />
      {tier && <input hidden name="tier_id" defaultValue={tier.id} />}
      <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid ?? ''} />
      {nectarPageBlock && <input hidden name="nectar_page_block" defaultValue={nectarPageBlock} />}
      <StatusInputs
        name="downgrade"
        errorMessage={t('account_actions.downgrade.toast.error')}
        successMessage={successToastMessage || t('account_actions.downgrade.toast.downgraded')}
      />
      {showSubmitButton && (
        <Button type="submit" variant="danger-light" isLoading={isSubmitting}>
          {isSubmitting ? `${t('shared.actions.downgrading')}…` : t('shared.cta.downgrade_subscription')}
        </Button>
      )}
      {children?.({ isLoading: isSubmitting, submitForm })}
    </fetcher.Form>
  );
};

export default DowngradeAction;
